/**
 * Redact secret-bearing route segments and query parameters before any URL or
 * path is handed to analytics, telemetry, the tracking console, or error
 * capture (AI-8118).
 *
 * Account-verification / password-reset flows land on routes whose token is a
 * PATH SEGMENT — `/login/<token>`, `/register/<token>`, `/reset/<token>`. That
 * token is a replayable credential; it must never reach PostHog, Sentry, or a
 * `page` property. These helpers replace the secret segment with a route
 * template (`/login/:token`) and redact the values of known-secret query/hash
 * parameters, while leaving ordinary paths untouched.
 */

// Routes whose first path segment after the prefix is a secret token. Keep in
// sync with the `/:token` routes in src/route/Routes.tsx.
const SECRET_ROUTE_PREFIXES = ["login", "register", "reset"] as const;

// Case-insensitive: routers match paths case-insensitively, so `/Login/<token>`
// must be caught too. The segment is captured so a known non-token sub-route can
// be left alone (below).
const SECRET_ROUTE_RE = new RegExp(`(^|/)(${SECRET_ROUTE_PREFIXES.join("|")})/([^/?#]+)`, "gi");

// Known non-token sub-routes under those prefixes (src/route/Routes.tsx):
// `/login/social` (SocialComplete) and `/register/callback/...` (OAuth callback).
// Excluded so a $pageview on them isn't mis-bucketed as `/login/:token` and
// distorting the login funnel.
const STATIC_SUBPATHS = new Set(["social", "callback"]);

// Query/hash parameters whose VALUE is a credential and must be redacted
// wherever they appear (compared case-insensitively).
export const SECRET_PARAM_NAMES = [
  "token",
  "login_token",
  // The social-login handoff (/login/social?t=<login_token>) carries the
  // one-time credential under this short name.
  "t",
  "access_token",
  "refresh_token",
  "id_token",
  "code",
  "state",
  "jti",
  "secret",
  "password",
  "api_key",
  "apikey",
  "key",
  "auth",
] as const;
const SECRET_PARAM_SET = new Set<string>(SECRET_PARAM_NAMES);

export const REDACTED = "<redacted>";

/**
 * Replace a secret token path segment with its route template.
 * `/login/abc123` -> `/login/:token`; `/dashboard`, bare `/login`, and known
 * sub-routes (`/login/social`, `/register/callback`) unchanged. Case-insensitive
 * and idempotent (`/login/:token` -> `/login/:token`).
 */
export function sanitizePath(pathname: string): string {
  if (!pathname) return pathname;
  return pathname.replace(SECRET_ROUTE_RE, (match, pre, route, seg) =>
    STATIC_SUBPATHS.has(String(seg).toLowerCase())
      ? match
      : `${pre}${String(route).toLowerCase()}/:token`
  );
}

function isSecretParam(name: string): boolean {
  return SECRET_PARAM_SET.has(name.toLowerCase());
}

function redactSearchParams(params: URLSearchParams): void {
  // Collect first — mutating while iterating URLSearchParams is unsafe.
  const secret = [...params.keys()].filter(isSecretParam);
  for (const name of secret) params.set(name, REDACTED);
}

// Redact secret params carried in the fragment (implicit-grant style
// `#access_token=…`), preserving non-secret hash content.
function redactHash(hash: string): string {
  const raw = hash.replace(/^#/, "");
  if (!raw.includes("=")) return hash;
  const parts = raw.split("&").map(kv => {
    const eq = kv.indexOf("=");
    if (eq === -1) return kv;
    const key = kv.slice(0, eq);
    let decoded = key;
    try {
      decoded = decodeURIComponent(key);
    } catch {
      // keep raw key for the check
    }
    return isSecretParam(decoded) ? `${key}=${REDACTED}` : kv;
  });
  return "#" + parts.join("&");
}

// Regex fallback for unparseable input: redact secret query/hash param VALUES in
// place. `name` is a fixed alphanumeric literal, so no escaping is needed.
function redactSecretParamsInString(input: string): string {
  let out = input;
  for (const name of SECRET_PARAM_NAMES) {
    out = out.replace(new RegExp(`([?&#]${name}=)[^&#]*`, "gi"), `$1${REDACTED}`);
  }
  return out;
}

// A secret param inside free text: an element's `href` in PostHog's
// `$elements_chain`, a link's text, a URL nested in another URL's query. The
// value runs to the next param, or to the first character that cannot be part
// of a quoted `$elements_chain` value or of a URL in prose: a double quote, a
// backslash (chain quotes are escaped as `\"`) or whitespace. Everything else,
// `;`, `'`, `<` and percent-encoded characters included, is value. Re-redacting
// a value is harmless: `<redacted>` (or `sanitizeUrl`'s `%3Credacted%3E`)
// becomes `<redacted>`.
// What starts a query or hash param, literally or percent-encoded, and what
// joins its name to its value. `CREDENTIAL_LINK_SELECTOR` is built from the
// same lists, so recordings block every link form this redaction catches.
export const PARAM_SEPARATORS = ["?", "&", "#"] as const;
export const ENCODED_PARAM_SEPARATORS = ["%3F", "%26", "%23"] as const;
export const PARAM_JOINERS = ["=", "%3D"] as const;
const SECRET_NAME_EQUALS = `(?:${SECRET_PARAM_NAMES.join("|")})(?:${PARAM_JOINERS.join("|")})`;
const VALUE_END = `"\\\\\\s`;
// After a literal `?`, `&` or `#` the value runs to the next literal one, so an
// encoded `%26` inside it (`token=%26abc`) is part of the value.
const LITERAL_SECRET_PARAM_RE = new RegExp(
  `([${PARAM_SEPARATORS.join("")}]${SECRET_NAME_EQUALS})[^&#${VALUE_END}]+`,
  "gi"
);
// In a percent-encoded URL nested in another URL's query the separators are
// encoded too, so the value also ends at `%26` or `%23`.
const ENCODED_SECRET_PARAM_RE = new RegExp(
  `((?:${ENCODED_PARAM_SEPARATORS.join("|")})${SECRET_NAME_EQUALS})(?:(?!%26|%23)[^&#${VALUE_END}])+`,
  "gi"
);

// `REDACTED` as `URLSearchParams` writes it, for values that must stay valid URLs.
const ENCODED_REDACTED = encodeURIComponent(REDACTED);

/**
 * Redact the value of every secret query/hash param found anywhere in `text`,
 * leaving the rest of the text untouched (AI-10227). Idempotent. `replacement`
 * is `REDACTED`, or `ENCODED_REDACTED` inside a URL.
 */
export function redactSecretParamsInText(text: string, replacement: string = REDACTED): string {
  return text
    .replace(LITERAL_SECRET_PARAM_RE, `$1${replacement}`)
    .replace(ENCODED_SECRET_PARAM_RE, `$1${replacement}`);
}

// Plain or null-prototype objects: the shapes event properties are built from.
// Class instances (`Date`, …) are left as they are.
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object") return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

// Apply `redactSecretParamsInText` to every string in a property value,
// descending into arrays and plain objects (PostHog's `$elements`). `copies`
// maps each array or object already entered to its copy, so a cycle (or a
// shared reference) points at the redacted copy instead of recursing forever.
function redactEmbeddedSecrets(value: unknown, copies = new WeakMap<object, unknown>()): unknown {
  if (typeof value === "string") return redactSecretParamsInText(value);
  if (!Array.isArray(value) && !isPlainObject(value)) return value;
  const seen = copies.get(value);
  if (seen !== undefined) return seen;
  if (Array.isArray(value)) {
    const copy: unknown[] = [];
    copies.set(value, copy);
    for (const item of value) copy.push(redactEmbeddedSecrets(item, copies));
    return copy;
  }
  const copy: Record<string, unknown> = {};
  copies.set(value, copy);
  for (const [key, nested] of Object.entries(value)) {
    copy[key] = redactEmbeddedSecrets(nested, copies);
  }
  return copy;
}

const ABSOLUTE_URL_RE = /^[a-z][a-z0-9+.-]*:\/\//i;

/**
 * Sanitize a full or relative URL: templatize secret path segments and redact
 * secret query/hash parameter values. Returns an absolute URL only when the
 * input was absolute; relative inputs come back as path+search+hash so we never
 * fabricate an origin the caller didn't have. Unparseable input still gets both
 * path templatization and a regex pass over secret params, so a raw token/
 * credential is never emitted.
 */
export function sanitizeUrl(rawUrl: string | null | undefined): string {
  if (!rawUrl) return rawUrl ?? "";
  const base = typeof window !== "undefined" ? window.location.origin : "http://localhost";
  let url: URL;
  try {
    url = new URL(rawUrl, base);
  } catch {
    return redactSecretParamsInString(sanitizePath(rawUrl));
  }
  url.pathname = sanitizePath(url.pathname);
  redactSearchParams(url.searchParams);
  // Hash may carry a token as a KV pair (`#access_token=…`) OR as a hash-router
  // path (`#/login/<token>`) — cover both.
  url.hash = redactHash(sanitizePath(url.hash));
  return ABSOLUTE_URL_RE.test(rawUrl) ? url.toString() : url.pathname + url.search + url.hash;
}

// A tracking property is URL/path-bearing if its key ends in url/pathname/
// referrer (case-insensitive). Covers PostHog's `$current_url`, every `$initial_*`,
// `$session_entry_*`, and `$prev_pageview_pathname` in one pass — no per-key list
// to keep in sync with the SDK.
function isUrlBearingKey(key: string): boolean {
  const k = key.toLowerCase();
  return k.endsWith("url") || k.endsWith("pathname") || k.endsWith("referrer");
}

/**
 * Return a copy of tracking properties with every URL/path property sanitized
 * and secret params redacted wherever else they appear, in nested arrays and
 * plain objects too (autocapture's `$elements_chain`, `$elements` and `$el_text`
 * carry clicked links' `href`). Recurses into PostHog's `$set` / `$set_once`
 * person-property blocks so nested `$initial_*` / `$session_entry_*` don't
 * bypass the sweep.
 */
export function sanitizeTrackingProps(
  properties: Record<string, unknown>
): Record<string, unknown> {
  return sanitizeProps(properties, new WeakMap());
}

// `inProgress` maps each properties block being sanitized to its output, so a
// `$set` that leads back to one of them points at that output instead of
// recursing forever.
function sanitizeProps(
  properties: Record<string, unknown>,
  inProgress: WeakMap<object, Record<string, unknown>>
): Record<string, unknown> {
  const out = { ...properties };
  inProgress.set(properties, out);
  for (const [key, value] of Object.entries(out)) {
    if (typeof value === "string" && value && isUrlBearingKey(key)) {
      // `sanitizeUrl` covers the URL's own params; the sweep covers a URL nested
      // in one of them, and writes the marker encoded as `sanitizeUrl` does, so
      // the value stays a valid URL in one format.
      out[key] = redactSecretParamsInText(sanitizeUrl(value), ENCODED_REDACTED);
    } else {
      out[key] = redactEmbeddedSecrets(value);
    }
  }
  // Our own `page` prop is a bare path.
  if (typeof out.page === "string") out.page = sanitizePath(out.page);
  // PostHog may also split the URL into standalone `$search` / `$hash`. These
  // carry a raw query/fragment; redact secret params + any hash-router path token
  // directly (no URL parse, so no spurious leading slash). `$host` is domain-only.
  for (const key of ["$search", "$hash"] as const) {
    const value = out[key];
    if (typeof value === "string" && value) {
      out[key] = redactSecretParamsInString(sanitizePath(value));
    }
  }
  for (const nestedKey of ["$set", "$set_once"] as const) {
    const nested = properties[nestedKey];
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      out[nestedKey] =
        inProgress.get(nested) ?? sanitizeProps(nested as Record<string, unknown>, inProgress);
    }
  }
  inProgress.delete(properties);
  return out;
}

// --- Sentry scrubbing -------------------------------------------------------
// Minimal structural shapes so this module keeps no hard @sentry/* dependency.
// Breadcrumb.data fields that hold a URL/path (navigation `to`/`from`,
// fetch/xhr `url`).
const BREADCRUMB_URL_FIELDS = ["url", "to", "from"] as const;

interface BreadcrumbLike {
  data?: Record<string, unknown>;
}

interface SpanLike {
  data?: Record<string, unknown>;
  description?: string;
}

interface SentryEventLike {
  request?: { url?: string; headers?: Record<string, unknown> };
  transaction?: string;
  breadcrumbs?: BreadcrumbLike[];
  spans?: SpanLike[];
  urls?: string[];
}

/** Scrub URL/path fields on a Sentry breadcrumb in place. */
export function sanitizeSentryBreadcrumb<T extends BreadcrumbLike>(breadcrumb: T): T {
  const data = breadcrumb?.data;
  if (data) {
    for (const field of BREADCRUMB_URL_FIELDS) {
      if (typeof data[field] === "string") data[field] = sanitizeUrl(data[field] as string);
    }
  }
  return breadcrumb;
}

/**
 * Scrub every URL-bearing field on a Sentry event in place — request URL +
 * `Referer`, transaction name, breadcrumbs, performance spans, and the replay
 * envelope's `urls` — so a secret route token can't reach Sentry via error
 * context, tracing (`beforeSendTransaction`), spans, or session replay. Wire to
 * `beforeSend`, `beforeSendTransaction`, and `addEventProcessor` (transactions
 * and replay events never pass through `beforeSend`).
 */
export function sanitizeSentryEvent<T extends SentryEventLike>(event: T): T {
  if (event.request?.url) event.request.url = sanitizeUrl(event.request.url);
  const headers = event.request?.headers;
  if (headers) {
    for (const key of Object.keys(headers)) {
      if (key.toLowerCase() === "referer" && typeof headers[key] === "string") {
        headers[key] = sanitizeUrl(headers[key] as string);
      }
    }
  }
  if (typeof event.transaction === "string") event.transaction = sanitizePath(event.transaction);
  event.breadcrumbs?.forEach(breadcrumb => sanitizeSentryBreadcrumb(breadcrumb));
  event.spans?.forEach(span => {
    if (span.data && typeof span.data.url === "string") {
      span.data.url = sanitizeUrl(span.data.url);
    }
    // description is often "GET /reset/<token>" — path-templatize it (full
    // sanitizeUrl would mangle the non-URL "GET " prefix).
    if (typeof span.description === "string") span.description = sanitizePath(span.description);
  });
  if (Array.isArray(event.urls)) {
    event.urls = event.urls.map(u => (typeof u === "string" ? sanitizeUrl(u) : u));
  }
  return event;
}
